import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type {
  BookingSlotPickerGetProQuery,
  BookingSlotPickerGetProQueryVariables,
} from '@gql/graphql';
import { useMemo } from 'react';

export const BOOKING_SLOT_PICKER_GET_PRO = graphql(`
  query BookingSlotPickerGetPro(
    $where: ProfessionalWhereInput!
    $serviceLanguage: String!
    $translationsWhere: ProTranslationWhereInput
    $serviceTranslationsWhere: ServiceTranslationWhereInput
    $workerServiceTranslationsWhere: WorkerServiceTranslationWhereInput
  ) {
    searchProfessionals(take: 1, where: $where) {
      professionals {
        id

        minHoursBeforeUpdateOrCancelBooking
        allowsAnyWorkerOption
        acceptedAnimals
        onlineBookingEnabled
        type

        workers {
          id
          fullName
        }

        services(language: $serviceLanguage) {
          id

          bookable
          service {
            id
            translations(where: $serviceTranslationsWhere) {
              title
            }
          }
          workerServices {
            animalSize
            animalType
            id
            workerId
            bookable
            multiAnimalsBookingEnabled
            multiAnimalsDuration

            translations(where: $workerServiceTranslationsWhere) {
              id
              preBookingWarning
            }
          }
        }

        translations(where: $translationsWhere) {
          id
          preBookingWarning
        }
      }
    }
  }
`);

export const useGetPro = ({ url, id }: { url?: string; id?: number }) => {
  const { language } = useRouter();

  const variables = useMemo((): BookingSlotPickerGetProQueryVariables => {
    const translationsWhere = { language: { equals: language } };
    return {
      where: {
        ...(!!id && { id: { equals: id } }),
        ...(url && {
          OR: [
            {
              translations: {
                some: {
                  url: { equals: url },
                },
              },
            },
            { proMongoId: { equals: url } },
          ],
        }),
      },
      translationsWhere,
      serviceTranslationsWhere: translationsWhere,
      workerServiceTranslationsWhere: translationsWhere,
      serviceLanguage: language,
    };
  }, [language, id, url]);

  const { data, error, loading, fetchMore, refetch } = useQuery<
    BookingSlotPickerGetProQuery,
    BookingSlotPickerGetProQueryVariables
  >(BOOKING_SLOT_PICKER_GET_PRO, {
    variables,
    notifyOnNetworkStatusChange: true,
    skip: !url && !id,
  });

  return {
    pro: data?.searchProfessionals.professionals?.[0],
    proError: error,
    proLoading: loading,
    proVariables: variables,
    proFetchMore: fetchMore,
    proRefetch: refetch,
  };
};
